import { format, formatDistanceToNow } from "date-fns";
import { ReasonForDecline } from "../../API";
import { useTranslation } from "react-i18next";
import { OptionCard } from "../../common-ui/OptionCard";
import {
  CircularProgress,
  Dialog,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  Tooltip
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import ChatIcon from "@mui/icons-material/Chat";
import { useEffect, useState } from "react";
import { ReasonForDeclineSelectFields } from "../../app/constants";
import { useDeclineAssessmentRequest } from "../AssessmentEvents/assessmentEventUtils";
import type { RestUser } from "../../custom-types";
import { QueryStatus, ToastStyle } from "../../app/types";
import { useAppDispatch } from "../../app/hooks";
import {
  setShowToast,
  setToastMessage,
  setToastStyle
} from "../../common-ui/toastSlice";
import { useAppSelector } from "../../app/hooks";
import { selectCurrentUser } from "../Users/usersSlice";
import { selectFeatureFlags } from "../Firebase/firebaseSlice";
import {
  AssessmentDetails,
  FlattenedAssessmentDetails
} from "../AssessmentEvents/assessmentEventsAPI";

type DeclineAssessmentButtonProps = {
  declineAssessmentRequest: (
    assessmentEventId: string,
    reason: ReasonForDecline
  ) => Promise<void>;
  assessmentDetails: AssessmentDetails | FlattenedAssessmentDetails;
  declineAssessmentStatus: string;
  declineAssessmentError?: string;
  refetchPendingAssessments?: () => void;
};

const declineAssessmentButton = ({
  declineAssessmentRequest,
  assessmentDetails,
  declineAssessmentStatus,
  declineAssessmentError,
  refetchPendingAssessments
}: DeclineAssessmentButtonProps) => {
  const [showDeclineModal, setShowDeclineModal] = useState(false);
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const handleModalClose = (event: object, reason: string) => {
    console.log("close reason:", reason);
    if (reason === "backdropClick") {
      return false;
    }
  };

  useEffect(() => {
    let newMessage;
    let newStatus: ToastStyle = "info";
    let hideModal;
    if (["succeeded", "failed"].includes(declineAssessmentStatus)) {
      if (declineAssessmentStatus === "succeeded") {
        newMessage = t("Assessment Declined");
        newStatus = "success";
        hideModal = true;
      }
      if (declineAssessmentStatus === "failed") {
        newMessage = t("Error declining assessment.");
        if (declineAssessmentError) {
          newMessage += t(declineAssessmentError);
        }
        newStatus = "error";
      }
      if (newMessage && newStatus) {
        dispatch(setToastMessage(newMessage));
        dispatch(setToastStyle(newStatus));
        dispatch(setShowToast(true));
      }
      if (hideModal && refetchPendingAssessments) {
        refetchPendingAssessments();
      }
      setShowDeclineModal(!hideModal);
    }
  }, [declineAssessmentStatus, declineAssessmentError]);

  return (
    <>
      <Dialog
        onClose={handleModalClose}
        onClick={(event) => {
          console.log("dialog clicked");
          event.stopPropagation();
        }}
        open={showDeclineModal}
        aria-labelledby="decline-assessment-title"
        aria-describedby="decline-assessment-description"
      >
        <DialogTitle id="decline-assessment-title">
          {t("Decline Assessment")}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="decline-assessment-description">
            {t("Please select the reason you are declining this Assessment.")}
          </DialogContentText>
          <List>
            {ReasonForDeclineSelectFields.map((reason, index) => {
              return (
                <ListItem disableGutters key={`${reason}-${index}`}>
                  <ListItemButton
                    disabled={declineAssessmentStatus === "loading"}
                    onClick={() =>
                      declineAssessmentRequest(
                        assessmentDetails.assessmentEventId,
                        reason.value
                      )
                    }
                  >
                    {reason.label}
                  </ListItemButton>
                </ListItem>
              );
            })}
            <ListItem disableGutters sx={{ fontWeight: "bold" }}>
              <ListItemButton
                disabled={declineAssessmentStatus === "loading"}
                onClick={() => setShowDeclineModal(false)}
              >
                {declineAssessmentStatus === "loading" ? (
                  <CircularProgress />
                ) : (
                  t("Cancel")
                )}
              </ListItemButton>
            </ListItem>
          </List>
        </DialogContent>
      </Dialog>
      <IconButton
        color="primary"
        aria-label="decline assessment"
        onClick={(event) => {
          console.log("decline assessment clicked");
          event.stopPropagation();
          event.nativeEvent.stopImmediatePropagation();
          setShowDeclineModal(true);
        }}
      >
        <DeleteIcon />
      </IconButton>
    </>
  );
};

type AssessmentCardProps = {
  assessmentDetails: AssessmentDetails | FlattenedAssessmentDetails;
  onClick: (assessmentEventId: string) => void;
  selfAssessment?: boolean;
  clickable?: boolean;
  declinable?: boolean;
  refetchPendingAssessments?: () => void;
};
export const AssessmentCard = ({
  assessmentDetails,
  onClick,
  selfAssessment,
  clickable,
  declinable,
  refetchPendingAssessments
}: AssessmentCardProps) => {
  const { t } = useTranslation();
  const currentAuthenticatedUser = useAppSelector(selectCurrentUser);
  const flags = useAppSelector(selectFeatureFlags);

  const {
    declineAssessmentRequest,
    status: declineAssessmentStatus,
    error: declineAssessmentError
  } = useDeclineAssessmentRequest();
  const capitalInitials = assessmentDetails.user.initials ?? "N/A";
  const activityEventDate =
    "activityEventDate" in assessmentDetails
      ? assessmentDetails.activityEventDate
      : null;
  const activityEventDateString = activityEventDate
    ? format(new Date(activityEventDate), "PPPppp")
    : null;
  const expirationDate = assessmentDetails.assessmentEventExpirationDate;
  const timeUntilExpiration = expirationDate
    ? formatDistanceToNow(new Date(expirationDate), { addSuffix: true })
    : "N/A";
  let title = assessmentDetails.user.fullName;
  const notes = assessmentDetails?.activityEventNotes;
  if (selfAssessment) {
    title += ` (${t("Self Assessment")})`;
  }
  const { activity_notes_visible } = flags;

  const subtitle = (
    <>
      {assessmentDetails.activityTitle}
      {activityEventDateString && (
        <>
          <br />
          {activityEventDateString}
        </>
      )}
      <br />
      {t("Expires")} {timeUntilExpiration}
      {activity_notes_visible && notes && (
        <Tooltip title={'"' + notes + '"'} arrow>
          <ChatIcon
            fontSize="small"
            sx={{
              verticalAlign: "middle",
              marginLeft: 1,
              color: "primary.main"
            }}
          />
        </Tooltip>
      )}
    </>
  );
  // If declinable is true, need to add the decline button to the rightContent
  return (
    <OptionCard
      avatarContent={capitalInitials}
      title={title}
      subtitle={subtitle}
      hasShadow
      filledBackground
      onClick={() => onClick(assessmentDetails.assessmentEventId)}
      clickable={clickable}
      rightContent={
        declinable
          ? declineAssessmentButton({
              declineAssessmentRequest,
              assessmentDetails,
              declineAssessmentStatus,
              declineAssessmentError,
              refetchPendingAssessments
            })
          : undefined
      }
    />
  );
};
